/**
 * BattleshipBoard React Component
 *
 * Renders an interactive 10×10 Battleship grid with column labels (A–J)
 * and row labels (1–10). Supports two grid types: 'ocean' (ship positions)
 * and 'tracking' (attack results). Includes hit/miss/sunk animations,
 * crosshair targeting, drag-and-drop placement preview, and accessibility.
 *
 * Requirements: 1.1, 1.3, 6.2, 6.3, 7.3, 7.4, 7.5, 8.3, 10.2, 10.5,
 *               17.1, 17.2, 17.3, 17.4, 17.6, 19.5, 19.6, 34.1, 34.2
 */

import React, { useState, useCallback, useRef } from 'react';
import { BattleshipEngine } from '../BattleshipEngine';
import {
  Coordinate,
  ShipPlacement,
  GamePhase,
  PlayerSide,
  OceanCellState,
  TrackingCellState,
  COLUMN_LETTERS,
  GRID_ROWS,
  GRID_COLS,
  coordinateToString,
  coordinatesEqual,
  BattleshipBoardProps,
} from '../types';
import './BattleshipBoard.css';

/**
 * BattleshipBoard renders a 10×10 grid and handles user interactions.
 */
export const BattleshipBoard: React.FC<BattleshipBoardProps> = ({
  engine,
  gridType,
  playerSide,
  interactive = true,
  isPlayerTurn = true,
  gamePhase,
  onCellClick,
  onShipDrop,
  highlightedCells,
}) => {
  const [hoveredCell, setHoveredCell] = useState<Coordinate | null>(null);
  const [dragPreview, setDragPreview] = useState<ShipPlacement | null>(null);
  const [dragValid, setDragValid] = useState(false);
  const [animatingCell, setAnimatingCell] = useState<{
    coordinate: Coordinate;
    result: 'hit' | 'miss' | 'sunk';
  } | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const boardRef = useRef<HTMLDivElement>(null);

  const typedEngine = engine as BattleshipEngine;
  const oceanGrid = typedEngine.getOceanGrid(playerSide);
  const trackingGrid = typedEngine.getTrackingGrid(playerSide);

  const canInteract =
    interactive &&
    isPlayerTurn &&
    gamePhase !== 'completed';

  const isTrackingInteractive =
    canInteract && gridType === 'tracking' && gamePhase === 'attack';

  const isOceanPlacement =
    gridType === 'ocean' && gamePhase === 'placement';

  // -----------------------------------------------------------------------
  // Cell click handler
  // -----------------------------------------------------------------------

  const handleCellClick = useCallback(
    (row: number, col: number) => {
      if (!canInteract) return;

      const coordinate: Coordinate = { row, col };

      if (gridType === 'tracking' && gamePhase === 'attack') {
        // Only allow clicking unknown cells on tracking grid
        if (trackingGrid[row][col] !== 'unknown') return;

        if (onCellClick) {
          onCellClick(coordinate);
        }

        setAnnouncement(
          `Attack at ${coordinateToString(coordinate)}`
        );
      } else if (gridType === 'ocean' && gamePhase === 'placement') {
        if (onCellClick) {
          onCellClick(coordinate);
        }
      }
    },
    [canInteract, gridType, gamePhase, trackingGrid, onCellClick]
  );

  // -----------------------------------------------------------------------
  // Hover handlers
  // -----------------------------------------------------------------------

  const handleCellMouseEnter = useCallback(
    (row: number, col: number) => {
      if (!canInteract) return;
      setHoveredCell({ row, col });
    },
    [canInteract]
  );

  const handleCellMouseLeave = useCallback(() => {
    setHoveredCell(null);
  }, []);

  // -----------------------------------------------------------------------
  // Drag-and-drop handlers for placement
  // -----------------------------------------------------------------------

  const handleDragOver = useCallback(
    (e: React.DragEvent, row: number, col: number) => {
      if (!isOceanPlacement || !onShipDrop) return;
      e.preventDefault();

      try {
        const shipData = e.dataTransfer.types.includes('text/plain');
        if (shipData) {
          setDragPreview({
            shipType: 'destroyer', // placeholder, actual type comes from drop
            startCell: { row, col },
            orientation: 'horizontal',
          });
          setDragValid(true);
        }
      } catch {
        // Drag data not accessible during dragover in some browsers
      }
    },
    [isOceanPlacement, onShipDrop]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, row: number, col: number) => {
      if (!isOceanPlacement || !onShipDrop) return;
      e.preventDefault();

      try {
        const data = e.dataTransfer.getData('text/plain');
        if (data) {
          const parsed = JSON.parse(data);
          const placement: ShipPlacement = {
            shipType: parsed.shipType,
            startCell: { row, col },
            orientation: parsed.orientation || 'horizontal',
          };
          onShipDrop(placement);
        }
      } catch {
        // Invalid drag data
      }

      setDragPreview(null);
      setDragValid(false);
    },
    [isOceanPlacement, onShipDrop]
  );

  const handleDragLeave = useCallback(() => {
    setDragPreview(null);
    setDragValid(false);
  }, []);

  // -----------------------------------------------------------------------
  // Trigger attack animation (called externally via ref or effect)
  // -----------------------------------------------------------------------

  const triggerAnimation = useCallback(
    (coordinate: Coordinate, result: 'hit' | 'miss' | 'sunk') => {
      setAnimatingCell({ coordinate, result });
      setTimeout(() => setAnimatingCell(null), 600);
    },
    []
  );

  // -----------------------------------------------------------------------
  // Cell helpers
  // -----------------------------------------------------------------------

  const isHighlighted = (row: number, col: number): boolean => {
    if (!highlightedCells) return false;
    return highlightedCells.some((c) => coordinatesEqual(c, { row, col }));
  };

  const isHoveredCell = (row: number, col: number): boolean => {
    if (!hoveredCell) return false;
    return hoveredCell.row === row && hoveredCell.col === col;
  };

  const isAnimating = (row: number, col: number): string | null => {
    if (!animatingCell) return null;
    if (
      animatingCell.coordinate.row === row &&
      animatingCell.coordinate.col === col
    ) {
      return animatingCell.result;
    }
    return null;
  };

  const getCellAriaLabel = (row: number, col: number): string => {
    const coord = coordinateToString({ row, col });

    if (gridType === 'ocean') {
      const cell = oceanGrid[row]?.[col];
      return cell === 'ship'
        ? `${coord}, your ship`
        : `${coord}, water`;
    }

    const cell = trackingGrid[row]?.[col];
    switch (cell) {
      case 'hit':
        return `${coord}, hit`;
      case 'miss':
        return `${coord}, miss`;
      case 'sunk':
        return `${coord}, sunk ship`;
      default:
        return `${coord}, unknown`;
    }
  };

  // -----------------------------------------------------------------------
  // Render ocean grid cell content
  // -----------------------------------------------------------------------

  const renderOceanCellContent = (row: number, col: number) => {
    const cell: OceanCellState = oceanGrid[row]?.[col] ?? 'empty';
    if (cell === 'ship') {
      return <div className="bs-cell__ship-indicator" />;
    }
    return null;
  };

  // -----------------------------------------------------------------------
  // Render tracking grid cell content
  // -----------------------------------------------------------------------

  const renderTrackingCellContent = (row: number, col: number) => {
    const cell: TrackingCellState = trackingGrid[row]?.[col] ?? 'unknown';
    switch (cell) {
      case 'hit':
        return <div className="bs-cell__hit-marker" />;
      case 'miss':
        return <div className="bs-cell__miss-marker" />;
      case 'sunk':
        return <div className="bs-cell__sunk-marker" />;
      default:
        return null;
    }
  };

  // -----------------------------------------------------------------------
  // Build cell class names
  // -----------------------------------------------------------------------

  const getCellClassName = (row: number, col: number): string => {
    const classes = ['bs-cell'];

    // Alternating water pattern
    if ((row + col) % 2 === 1) {
      classes.push('bs-cell--alt');
    }

    if (gridType === 'ocean') {
      const cell = oceanGrid[row]?.[col];
      if (cell === 'ship') {
        classes.push('bs-cell--ship');
      }
    } else {
      const cell = trackingGrid[row]?.[col];
      if (cell === 'sunk') {
        classes.push('bs-cell--sunk');
      }
      if (cell !== 'unknown') {
        classes.push('bs-cell--non-interactive');
      }
    }

    // Targeting crosshair on tracking grid hover
    if (
      isTrackingInteractive &&
      isHoveredCell(row, col) &&
      trackingGrid[row]?.[col] === 'unknown'
    ) {
      classes.push('bs-cell--targeting');
    }

    // Drag preview
    if (dragPreview && isOceanPlacement) {
      classes.push(dragValid ? 'bs-cell--drag-valid' : 'bs-cell--drag-invalid');
    }

    // Highlighted cells
    if (isHighlighted(row, col)) {
      classes.push('bs-cell--highlighted');
    }

    // Animation classes
    const animResult = isAnimating(row, col);
    if (animResult === 'hit') {
      classes.push('bs-cell--animating-hit');
    } else if (animResult === 'miss') {
      classes.push('bs-cell--animating-miss');
    } else if (animResult === 'sunk') {
      classes.push('bs-cell--animating-sunk');
    }

    if (!canInteract) {
      classes.push('bs-cell--non-interactive');
    }

    return classes.join(' ');
  };

  // -----------------------------------------------------------------------
  // Render column labels
  // -----------------------------------------------------------------------

  const renderColumnLabels = () => (
    <div className="bs-board__col-labels">
      <div className="bs-board__col-label bs-board__col-label--corner" />
      {COLUMN_LETTERS.map((letter) => (
        <div key={letter} className="bs-board__col-label">
          {letter}
        </div>
      ))}
    </div>
  );

  // -----------------------------------------------------------------------
  // Render grid rows with row labels
  // -----------------------------------------------------------------------

  const renderGrid = () => {
    const rows = [];
    for (let row = 0; row < GRID_ROWS; row++) {
      // Row label
      rows.push(
        <div key={`row-label-${row}`} className="bs-board__row-label">
          {row + 1}
        </div>
      );

      // Row cells
      for (let col = 0; col < GRID_COLS; col++) {
        rows.push(
          <div
            key={`${row}-${col}`}
            className={getCellClassName(row, col)}
            onClick={() => handleCellClick(row, col)}
            onMouseEnter={() => handleCellMouseEnter(row, col)}
            onMouseLeave={handleCellMouseLeave}
            onTouchStart={(e) => {
              if (!canInteract) return;
              e.preventDefault();
              handleCellClick(row, col);
            }}
            onDragOver={(e) => handleDragOver(e, row, col)}
            onDrop={(e) => handleDrop(e, row, col)}
            onDragLeave={handleDragLeave}
            role="gridcell"
            aria-label={getCellAriaLabel(row, col)}
            data-row={row}
            data-col={col}
          >
            {gridType === 'ocean'
              ? renderOceanCellContent(row, col)
              : renderTrackingCellContent(row, col)}
          </div>
        );
      }
    }
    return rows;
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const boardTitle = gridType === 'ocean' ? 'Your Fleet' : 'Enemy Waters';

  return (
    <div className="bs-board-container">
      <div
        className={`bs-board ${!canInteract ? 'bs-board--disabled' : ''}`}
        ref={boardRef}
        role="grid"
        aria-label={`Battleship ${boardTitle}`}
      >
        <div className="bs-board__title">{boardTitle}</div>

        {renderColumnLabels()}

        <div className="bs-board__grid-wrapper">
          {renderGrid()}
        </div>

        {/* ARIA Live Region */}
        <div
          className="bs-board__announcer"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {announcement}
        </div>
      </div>
    </div>
  );
};

export default BattleshipBoard;
